import { combineReducers, createReducer, type UnknownAction } from '@reduxjs/toolkit';

import { replaceVehicleValue } from 'Core/actions/fitmentSearch/index';
import { loadMore, skipRequest } from 'Core/actions/request';
import { setResponse } from 'Core/actions/response';
import { viewChangeRequested } from 'Core/actions/show';
import { fromPayload, getRequestMeta } from 'Core/reducers/common';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import uiConfig from 'Models/uiConfig/uiConfig';
import { loadView } from 'Utils/userStorage';

const initStates = {
  infiniteScrollLoading: false,
  resultsLoading: false,
  view: loadView() || uiConfig.defaultView,
};

type LoadingReducerConfig = {
  sendRequestIgnoreTypes: 'all' | string[];
  discardTypes: string[];
};

const makeDefaultLoadingReducer =
  ({ sendRequestIgnoreTypes, discardTypes }: LoadingReducerConfig) =>
  (state: boolean, action: UnknownAction): boolean => {
    const { sendRequest } = getRequestMeta(action);
    if (sendRequest) {
      return sendRequestIgnoreTypes === 'all' || sendRequestIgnoreTypes.includes(action.type) ? state : true;
    }
    return discardTypes.includes(action.type) ? false : state;
  };

const isolatedVehicleIgnoreTypes: readonly string[] = fitmentSearchConfig.isAnyVehicleWidgetIsolated
  ? [replaceVehicleValue.type]
  : [];

const infiniteScrollLoadingTypes = {
  discardTypes: [setResponse.type, skipRequest.type],
  sendRequestIgnoreTypes: 'all',
} satisfies LoadingReducerConfig;

const loadingTypes = {
  discardTypes: [setResponse.type, skipRequest.type],
  sendRequestIgnoreTypes: [loadMore.type, ...isolatedVehicleIgnoreTypes],
} satisfies LoadingReducerConfig;

export default combineReducers({
  infiniteScrollLoading: createReducer(initStates.infiniteScrollLoading, (builder) =>
    builder
      .addCase(loadMore, () => true)
      .addDefaultCase(makeDefaultLoadingReducer(infiniteScrollLoadingTypes)),
  ),
  resultsLoading: createReducer(initStates.resultsLoading, (builder) =>
    builder.addDefaultCase(makeDefaultLoadingReducer(loadingTypes)),
  ),
  view: createReducer(initStates.view, (builder) => builder.addCase(viewChangeRequested, fromPayload)),
});
